import React, { useMemo } from "react";
import { modelsFixedKeys } from "domains/models/api/endpoints";
import TransferModal from "domains/models/components/ModelShow/Header/TransferModal";
import { getIsBaseModel, getIsModelFlux } from "domains/models/utils";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import ButtonWithModal from "domains/ui/components/ButtonWithModal";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import {
  GetModelsByModelIdApiResponse,
  useDeleteModelsByModelIdMutation,
} from "infra/api/generated/api";

import {
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuList,
  useDisclosure,
} from "@chakra-ui/react";

interface ActionMenuProps {
  isOnLoraCatalog: boolean;
  canRetrain: boolean;
  model: GetModelsByModelIdApiResponse["model"];
  onCopyClick: () => void;
  onCopyTrainedClick: () => void;
  onEditBlendClick: () => void;
  onExportClick: () => void;
  onRemoveClick: () => void;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  isOnLoraCatalog,
  canRetrain,
  model,
  onCopyClick,
  onCopyTrainedClick,
  onEditBlendClick,
  onExportClick,
  onRemoveClick,
}) => {
  const { selectedProject } = useTeamContext();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const isModelOwned = model?.ownerId === selectedProject.id;

  const isCustomModel = model.type.includes("custom");
  const isModelTrained = model.status === "trained";
  const isBaseModel = getIsBaseModel(model);
  const isModelFlux = getIsModelFlux(model);
  const isModelComposition = model.type.includes("-composition");
  const isExportable = isModelOwned && isModelTrained && !isCustomModel;

  const hasAvailableOptions = useMemo(() => {
    if (isOnLoraCatalog || isBaseModel) {
      return false;
    }

    return canRetrain || isModelComposition || isExportable || isModelOwned;
  }, [
    canRetrain,
    isModelComposition,
    isExportable,
    isModelOwned,
    isOnLoraCatalog,
    isBaseModel,
  ]);

  const [, { isLoading: deleteModelIsLoading }] =
    useDeleteModelsByModelIdMutation({
      fixedCacheKey: modelsFixedKeys.deleteModel,
    });

  if (!hasAvailableOptions) {
    return null;
  }

  return (
    <Menu isOpen={isOpen} onClose={onClose} onOpen={onOpen}>
      <MenuButton as={IconButton} size="sm" variant="secondary">
        <Icon id="Ui/Dots" />
      </MenuButton>

      <MenuList zIndex="popover">
        {canRetrain && (
          <MenuItem
            onClick={onCopyClick}
            text="Retrain"
            iconId="Ui/RefreshAlt"
          />
        )}
        {isModelComposition && (
          <MenuItem onClick={onEditBlendClick} text="Edit" iconId="Ui/Edit" />
        )}
        {isExportable && (
          <>
            <MenuItem
              onClick={onCopyTrainedClick}
              text="Duplicate"
              iconId="Ui/Copy"
            />
            <MenuItem
              {...(isModelFlux
                ? {
                    as: ButtonWithModal,
                    onConfirm: async () => {
                      void onExportClick();
                    },
                    modalHeader: "Export Model",
                    modalBody: (
                      <>
                        <span>
                          {
                            "The use of this LoRA outside of Scenario is subject to the same license restrictions as the base FluxDev model."
                          }
                        </span>
                        <br />
                        <br />
                        <span>
                          {
                            "By downloading this LoRA, you agree to be bound by "
                          }
                        </span>
                        <Link
                          color="primary.500"
                          href="https://github.com/black-forest-labs/flux/blob/main/model_licenses/LICENSE-FLUX1-dev"
                          target="_blank"
                        >
                          these terms
                        </Link>
                        <span>
                          {
                            ". Redistribution or use outside these terms is prohibited."
                          }
                        </span>
                      </>
                    ),
                    modalConfirmMessage: "Export",
                  }
                : {
                    onClick: onExportClick,
                  })}
              text="Export"
              iconId="Ui/Download"
            />
          </>
        )}
        {isModelOwned && (
          <>
            {!isCustomModel && (
              <>
                <TransferModal model={model} onClick={onClose} />
                <MenuDivider />
              </>
            )}
            <MenuItem
              as={ButtonWithModal}
              colorScheme="danger"
              iconId="Ui/Trash"
              isModalConfirmButtonLoading={deleteModelIsLoading}
              modalBody="Are you sure you want to delete this model?"
              modalHeader="Delete Model"
              onConfirm={onRemoveClick}
              text="Delete"
            />
          </>
        )}
      </MenuList>
    </Menu>
  );
};

export default ActionMenu;
